import { useEffect, useState } from 'react';
import type { Campaign } from '@/types/portal';
import { api } from '@/utils/api';
import { formatMoney, pluralize } from '@/utils/format';

/** A creator flagged by automation, as `GET /dashboard` returns them. */
export interface FlaggedCreator {
  id: string;
  creatorName: string;
  creatorId: string;
  avatar: string | null;
  campaign: string;
  campaignId: string;
  campaignStatus: string;
  action: string;
  flag: string;
  /** The campaign's end date, e.g. "5 Oct 2026". */
  due: string;
}

/** An item sitting in the approval queue, whatever its type. */
export interface ApprovalItem {
  id: string;
  type: string;
  title: string;
  detail: string;
  amount: string;
  status: string;
  /** "Due today", "Overdue", "Due 7 Oct 2026", or how long it has waited. */
  due: string;
  /** Where to act on it. */
  link?: string;
}

export interface DashboardInsight {
  id: string;
  title: string;
  body: string;
  cta: string;
  link: string;
}

export interface DashboardAlert {
  id: string;
  title: string;
  detail: string;
  severity: 'high' | 'medium' | 'low';
  icon: string;
}

/**
 * Where each alert is acted on, by its id (DashboardController::buildAlerts sends
 * no link; the website shows alerts as text). The page opens filtered to the items
 * the alert counts. `permission` = the page's own, so a role that can't open it
 * gets the alert as plain text.
 */
const ALERT_LINKS: Record<string, { path: string; permission: string }> = {
  'al-failed-payout': { path: '/payouts?status=Failed', permission: 'payouts.view' },
  'al-overdue': { path: '/campaigns?attention=1', permission: 'campaigns.view' },
  // Creators still invited or yet to open the link sit on recruiting campaigns.
  'al-kyc': { path: '/campaigns?status=Recruiting', permission: 'campaigns.view' },
  'al-invoices': { path: `/invoices?status=${encodeURIComponent('Pending review')}`, permission: 'invoices.view' },
};

/** The alert's page, when the signed-in role can open it. */
export function alertLink(alert: DashboardAlert, can: (permission: string) => boolean): string | null {
  const link = ALERT_LINKS[alert.id];
  return link && can(link.permission) ? link.path : null;
}

/** A bulk batch that is queued to release on a chosen day and time. */
export interface ScheduledBatchSummary {
  id: string;
  reference: string;
  recipientCount: number;
  total: number;
  currency: string;
  /** Human label, e.g. "18 Sep 2026, 09:00". */
  scheduledFor: string;
}

/** An open campaign collecting interest, with how many new applications it holds. */
export interface OpenCampaignApplications {
  id: string;
  name: string;
  count: number;
}

/** Everything the dashboard shows, from `GET /dashboard` (server/dashboard.ts). */
export interface DashboardData {
  stats: {
    activeCampaigns: number;
    inReviewCampaigns: number;
    marketsInFlight: number;
    inFlightMarkets: string[];
    creatorsNeedingAttention: number;
    pendingApprovals: number;
    /** Pending approvals due today or already overdue. */
    approvalsDueToday: number;
    approvalsHint: string;
  };
  /** Completed payouts this month and last (there is no monthly spend plan). */
  spend: { thisMonth: number; lastMonth: number; currency: string };
  campaigns: Campaign[];
  creatorActions: FlaggedCreator[];
  notYetActive: FlaggedCreator[];
  approvals: ApprovalItem[];
  dueToday: ApprovalItem[];
  insights: DashboardInsight[];
  alerts: DashboardAlert[];
  /** New applications waiting on open campaigns, biggest pool first. */
  applications: { count: number; campaigns: OpenCampaignApplications[] };
  scheduledBatches: ScheduledBatchSummary[];
}

export interface DashboardKpi {
  label: string;
  value: string;
  icon: string;
  tone: 'primary' | 'accent' | 'secondary' | 'neutral';
  delta?: string;
  deltaPositive?: boolean;
  hint?: string;
}

/** Loads the dashboard once; `reload` fetches it again. */
/** How often an open dashboard quietly fetches fresh figures. */
const DASHBOARD_REFRESH_MS = 2 * 60 * 1000;

export function useDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError('');
    api<DashboardData>('/dashboard')
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setUpdatedAt(Date.now());
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Refresh in the background while the tab is visible, and straight away when
  // the viewer comes back to a tab whose figures have gone stale.
  useEffect(() => {
    const refresh = () => setReloadKey((key) => key + 1);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, DASHBOARD_REFRESH_MS);
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        updatedAt !== null &&
        Date.now() - updatedAt > DASHBOARD_REFRESH_MS
      ) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [updatedAt]);

  return { data, error, updatedAt, reload: () => setReloadKey((key) => key + 1) };
}

function formatMillions(value: number, currency: string): string {
  return value >= 1000000 ? `${currency} ${(value / 1000000).toFixed(2)}M` : formatMoney(value, currency);
}

export function getDashboardKpis(data: DashboardData): DashboardKpi[] {
  const { stats, spend } = data;
  const spendChange = spend.lastMonth ? ((spend.thisMonth - spend.lastMonth) / spend.lastMonth) * 100 : null;
  return [
    {
      label: 'Active campaigns',
      value: String(stats.activeCampaigns),
      icon: 'ri-megaphone-line',
      tone: 'primary',
      delta: `${stats.marketsInFlight} markets`,
      deltaPositive: true,
      hint: `${stats.inReviewCampaigns} in review`,
    },
    {
      label: 'Creators needing attention',
      value: stats.creatorsNeedingAttention.toLocaleString(),
      icon: 'ri-user-star-line',
      tone: 'accent',
      delta: `${stats.marketsInFlight} markets`,
      deltaPositive: false,
      hint: `Flagged across ${stats.activeCampaigns} ${pluralize(stats.activeCampaigns, 'active campaign')}`,
    },
    {
      label: 'Spend this month',
      value: formatMillions(spend.thisMonth, spend.currency),
      icon: 'ri-line-chart-line',
      tone: 'secondary',
      delta: spendChange === null ? undefined : `${Math.abs(spendChange).toFixed(1)}%`,
      deltaPositive: spendChange === null ? undefined : spendChange >= 0,
      hint: spend.lastMonth
        ? `vs ${formatMillions(spend.lastMonth, spend.currency)} last month`
        : 'No payouts last month',
    },
    {
      label: 'Pending approvals',
      value: String(stats.pendingApprovals),
      icon: 'ri-checkbox-circle-line',
      tone: 'neutral',
      delta: `${stats.approvalsDueToday} due now`,
      deltaPositive: false,
      hint: stats.approvalsHint || 'Nothing waiting',
    },
  ];
}
